import { useToast } from '@ui/toasts/useToastManager'
import Button from '@ui/buttons/Button'
import ButtonsFixed from '@ui/buttons/ButtonsFixed'
import { springButtonsFixed } from '@ui/buttons/ButtonsFixed/styles'
import useCartState from '@order/hooks/useCartState'
import { useOrderState } from '@order/hooks/useOrderState'
import { router } from 'expo-router'
import { useConfirmCustomer } from '@user/customer/confirmation/useConfirmCustomer'
import useCustomerInvoice from 'hooks/useCustomerInvoice'
import useSession, { useResolvedDealsContext } from 'hooks/useSession'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'
import { InvoiceFormSchema } from 'utils/vatSchema'

const MOVE = 200

interface Props {
  isLoading: boolean
  onPoliciesRequired?: () => void
}

export default function ConfirmButtons({
  isLoading,
  onPoliciesRequired,
}: Props) {
  const { t } = useTranslation(['ui', 'validation'])
  const visible = useSharedValue(1)
  const toast = useToast()
  const confirm = useConfirmCustomer()
  const { isConfirmed, handleConfirm } = confirm
  const setInfo = useSession((s) => s.setInfo)
  const addOrder = useCartState((s) => s.addOrder)

  const { data: invoice } = useCustomerInvoice()
  const invoiceEnabled = useOrderState((s) => s.order.invoice)

  const isInvoiceValid = (() => {
    if (!invoiceEnabled) {
      return true
    }

    return InvoiceFormSchema.safeParse(invoice).success
  })()

  const order = useOrderState((s) => s.order)
  const result = useOrderState((s) => s.result)
  const discounts = useOrderState((s) => s.discounts)

  const deals = useResolvedDealsContext()

  const pendingOrder = useSession((s) => s.pendingOrder)

  const onNext = () => {
    if (!result) {
      return
    }
    if (!isInvoiceValid) {
      setInfo({ isCustomerDetailsConfirmed: false })
      toast({
        variant: 'error',
        text: t('validation:confirmation.invoice'),
      })
      return
    }
    if (!isConfirmed) {
      handleConfirm({ onPoliciesRequired })
      return
    }
    addOrder({
      order,
      primaryId: pendingOrder?.savedOrderId,
      result,
      discounts,
      deals,
    })
    router.dismissAll()
    router.replace('/checkout')
  }

  const styleConfirm = useAnimatedStyle(() => {
    return {
      transform: [
        { translateY: interpolate(visible.value, [0, 1], [MOVE, 0]) },
      ],
    }
  })

  const styleNext = useAnimatedStyle(() => {
    return {
      transform: [
        { translateY: interpolate(visible.value, [0, 1], [0, MOVE]) },
      ],
    }
  })

  useEffect(() => {
    visible.set(withSpring(isConfirmed ? 0 : 1, springButtonsFixed))
  }, [isConfirmed, visible])

  const onConfirm = () => {
    if (!isInvoiceValid) {
      setInfo({ isCustomerDetailsConfirmed: false })
      toast({
        variant: 'error',
        text: t('validation:confirmation.invoice'),
      })
      return
    }
    handleConfirm({ onPoliciesRequired })
  }

  return (
    <ButtonsFixed>
      <Animated.View className="absolute bottom-0" style={styleConfirm}>
        <Button
          variant="outlined"
          label={t('ui:confirm')}
          onPress={onConfirm}
        />
      </Animated.View>
      <Animated.View className="absolute bottom-0" style={styleNext}>
        <Button
          variant="filled"
          label={t('ui:next')}
          onPress={onNext}
          iconEnd="arrow-narrow-right"
          loading={isLoading}
        />
      </Animated.View>
    </ButtonsFixed>
  )
}
